import type { Renderer2, RendererFactory2 } from '@angular/core';

import type { OverlayRef, ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import type { ModalOptions } from './modal-options';

/**
 * Strategy that will allow modal to scroll with a page when modal extends past view port
 */
export class ModalPageScrollStrategy implements ScrollStrategy {
  private static readonly MODAL_OPEN_CLASS: string = 'td-wb-modal-open';
  private static readonly PAGE_SCROLL_CLASS: string = 'td-wb-modal-page-scroll';
  private static modalCount: number = 0;
  private static pageScrollCount: number = 0;
  private static readonly sizeCounts: Map<string, number> = new Map<string, number>();

  private readonly renderer: Renderer2;
  private isEnabled: boolean = false;
  private readonly blockStrategy: ScrollStrategy;
  private scrollPosition: number = 0;
  private overlayRef: OverlayRef;
  private resizeObserver: ResizeObserver | null = null;
  private lastIsTall: boolean | null = null;
  private lastRequiredHeight: number = 0;
  private readonly onWindowResize: () => void = (): void => this.updateScrollState();
  private resizeObserverPending: boolean = false;
  private originalWrapperParent: HTMLElement | null = null;
  private originalBackdropParent: HTMLElement | null = null;

  constructor(private readonly document: Document,
              private readonly window: Window,
              rendererFactory: RendererFactory2,
              public modalOptions: ModalOptions,
              scrollStrategyOptions: ScrollStrategyOptions
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.blockStrategy = scrollStrategyOptions.block();
  }

  attach(overlayRef: OverlayRef): void {
    this.overlayRef = overlayRef;
    if (this.blockStrategy !== undefined && typeof this.blockStrategy.attach === 'function') {
      this.blockStrategy.attach(overlayRef);
    }
  }

  enable(): void {
    if (this.isEnabled) {
      return;
    }
    this.isEnabled = true;

    this.scrollPosition = this.window.scrollY || this.document.documentElement.scrollTop;

    if (ModalPageScrollStrategy.modalCount === 0) {
      this.renderer.addClass(this.document.body, ModalPageScrollStrategy.MODAL_OPEN_CLASS);
    }
    ModalPageScrollStrategy.modalCount++;

    const sizeClass = `${ModalPageScrollStrategy.MODAL_OPEN_CLASS}--${this.modalOptions.size}`;
    const sizeCount = ModalPageScrollStrategy.sizeCounts.get(this.modalOptions.size) || 0;
    if (sizeCount === 0) {
      this.renderer.addClass(this.document.body, sizeClass);
    }
    ModalPageScrollStrategy.sizeCounts.set(this.modalOptions.size, sizeCount + 1);

    this.updateScrollState();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleScrollStateUpdate());
      this.resizeObserver.observe(this.overlayRef.overlayElement);
    }
    this.window.addEventListener('resize', this.onWindowResize);
  }

  disable(): void {
    if (!this.isEnabled) {
      return;
    }
    this.isEnabled = false;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.window.removeEventListener('resize', this.onWindowResize);

    this.setPageScrollMode(false);

    ModalPageScrollStrategy.modalCount--;
    if (ModalPageScrollStrategy.modalCount === 0) {
      this.renderer.removeClass(this.document.body, ModalPageScrollStrategy.MODAL_OPEN_CLASS);
    }

    const sizeClass = `${ModalPageScrollStrategy.MODAL_OPEN_CLASS}--${this.modalOptions.size}`;
    const sizeCount = ModalPageScrollStrategy.sizeCounts.get(this.modalOptions.size) || 0;
    if (sizeCount === 1) {
      this.renderer.removeClass(this.document.body, sizeClass);
      ModalPageScrollStrategy.sizeCounts.delete(this.modalOptions.size);
    } else if (sizeCount > 1) {
      ModalPageScrollStrategy.sizeCounts.set(this.modalOptions.size, sizeCount - 1);
    }

    // Only call blockStrategy.disable() if no other modals are using it (block mode)
    const blockModeCount = ModalPageScrollStrategy.modalCount - ModalPageScrollStrategy.pageScrollCount;
    if (this.blockStrategy !== undefined && blockModeCount === 0) {
      this.blockStrategy.disable();
    }

    // Only restore scroll position if no other modals are in page scroll mode
    if (ModalPageScrollStrategy.pageScrollCount === 0) {
      // Restore prior scroll position using spec-compliant option.
      this.window.scrollTo({ left: 0, top: this.scrollPosition, behavior: 'auto' });
    } else {
      // If other modals are still in page scroll mode, ensure scroll block classes are removed
      this.renderer.removeClass(this.document.body, 'cdk-global-scrollblock');
      this.renderer.removeClass(this.document.documentElement, 'cdk-global-scrollblock');

      // Trigger a resize event to force other modals to recalculate their scroll state
      this.window.dispatchEvent(new Event('resize'));
    }
  }

  private updateScrollState(): void {
    const isXS = this.modalOptions.size === 'extra-small';
    const modalElement = this.overlayRef?.overlayElement;

    // If this is an extra-small modal and page scroll is already active from another modal,
    // don't interfere with the existing page scroll state
    if (isXS && ModalPageScrollStrategy.pageScrollCount > 0) {
      // Set lastIsTall to false so disable() knows this modal is not in tall mode
      this.lastIsTall = false;
      return;
    }

    if (isXS || !modalElement) {
      this.setPageScrollMode(false);
      return;
    }

    const viewportHeight = this.window.innerHeight;
    const modalHeight = modalElement.getBoundingClientRect().height;
    const requiredHeight = modalHeight + 60;

    const buffer = 20;
    const isTall = this.lastIsTall
      ? requiredHeight > viewportHeight - buffer
      : requiredHeight > viewportHeight;

    this.setPageScrollMode(isTall, requiredHeight);
  }

  private setPageScrollMode(enabled: boolean, requiredHeight: number = 0): void {
    const heightChanged: boolean = Math.abs(requiredHeight - this.lastRequiredHeight) >= 5;
    if (enabled === this.lastIsTall && (!enabled || !heightChanged)) {
      return;
    }

    const wasEnabled: boolean | null = this.lastIsTall;
    this.lastIsTall = enabled;
    this.lastRequiredHeight = requiredHeight;

    if (enabled) {
      this.enterPageScrollMode(wasEnabled, requiredHeight);
    } else if (wasEnabled) {
      this.exitPageScrollMode();
    }
  }

  private enterPageScrollMode(wasEnabled: boolean | null, requiredHeight: number): void {
    if (!wasEnabled) {
      const previouslyFocusedElement: Element | null = this.document.activeElement;
      ModalPageScrollStrategy.pageScrollCount++;
      if (ModalPageScrollStrategy.pageScrollCount === 1) {
        this.applyGlobalPageScrollStyles();
      }
      this.scrollIfRequired(previouslyFocusedElement);
    }

    this.applyLocalPageScrollStyles(requiredHeight);
  }

  private exitPageScrollMode(): void {
    this.removeLocalPageScrollStyles();
    ModalPageScrollStrategy.pageScrollCount--;

    if (ModalPageScrollStrategy.pageScrollCount === 0) {
      this.removeGlobalPageScrollStyles();
    }
  }

  private applyGlobalPageScrollStyles(): void {
    const { body, documentElement }: { body: HTMLElement; documentElement: HTMLElement } = this.document;
    this.renderer.addClass(body, ModalPageScrollStrategy.PAGE_SCROLL_CLASS);
    this.renderer.addClass(documentElement, ModalPageScrollStrategy.PAGE_SCROLL_CLASS);

    this.renderer.setStyle(body, 'height', 'auto');
    this.renderer.setStyle(documentElement, 'height', 'auto');

    this.window.scrollTo({ left: 0, top: 0, behavior: 'auto' });

    if (this.blockStrategy) {
      this.blockStrategy.disable();
    }
    this.renderer.removeClass(body, 'cdk-global-scrollblock');
    this.renderer.removeClass(documentElement, 'cdk-global-scrollblock');
  }

  private removeGlobalPageScrollStyles(): void {
    const { body, documentElement }: { body: HTMLElement; documentElement: HTMLElement } = this.document;
    this.renderer.removeClass(body, ModalPageScrollStrategy.PAGE_SCROLL_CLASS);
    this.renderer.removeClass(documentElement, ModalPageScrollStrategy.PAGE_SCROLL_CLASS);
    this.renderer.removeStyle(body, 'min-height');
    this.renderer.removeStyle(body, 'height');
    this.renderer.removeStyle(documentElement, 'height');

    if (this.blockStrategy) {
      this.blockStrategy.enable();
    }
  }

  private applyLocalPageScrollStyles(requiredHeight: number): void {
    const minHeight = `${requiredHeight + 20}px`;
    this.renderer.setStyle(this.document.body, 'min-height', minHeight);

    const backdrop: HTMLElement | null = this.overlayRef?.backdropElement;
    if (backdrop) {
      this.renderer.addClass(backdrop, 'td-wb-modal-page-scroll-item');
      this.renderer.setStyle(backdrop, 'min-height', `${this.document.body.offsetHeight}px`);
    }

    const wrapper: HTMLElement | null = this.overlayRef?.overlayElement?.parentElement;
    if (wrapper) {
      this.renderer.addClass(wrapper, 'td-wb-modal-page-scroll-item');
      this.renderer.setStyle(wrapper, 'min-height', minHeight);
    }

    // Reparent wrapper + backdrop out of the CDK overlay container and into <body>.
    // The CDK overlay container must stay `position: fixed` for connected overlays
    // (mat-select, mat-autocomplete, tooltip, etc.) to anchor correctly. In page-scroll
    // mode the modal itself is taller than the viewport, so it needs to live in the
    // document-flow scroll context (body) instead.
    this.reparentWithoutTransitionReplay(wrapper, backdrop, true);
  }

  private removeLocalPageScrollStyles(): void {
    const backdrop: HTMLElement | null = this.overlayRef?.backdropElement;
    const wrapper: HTMLElement | null = this.overlayRef?.overlayElement?.parentElement;

    // Restore wrapper + backdrop to the CDK overlay container BEFORE stripping
    // page-scroll classes so CDK's dispose path finds them in their expected location.
    this.reparentWithoutTransitionReplay(wrapper, backdrop, false);

    if (backdrop) {
      this.renderer.removeClass(backdrop, 'td-wb-modal-page-scroll-item');
      this.renderer.removeStyle(backdrop, 'min-height');
    }

    if (wrapper) {
      this.renderer.removeClass(wrapper, 'td-wb-modal-page-scroll-item');
      this.renderer.removeStyle(wrapper, 'min-height');
    }
  }

  /**
   * Coordinates moving the modal's overlay wrapper and backdrop between the
   * `.cdk-overlay-container` and `<body>` (or back again) without letting the
   * browser replay CSS transitions or `@starting-style` rules on the re-inserted
   * subtree.
   *
   * Why the reparent is needed in the first place:
   *   Angular Material's connected overlays — `mat-select`, `mat-autocomplete`,
   *   `matTooltip`, `matMenu`, etc. — position their panels via CDK's
   *   `FlexibleConnectedPositionStrategy`. That strategy reads the origin
   *   element's `getBoundingClientRect()` (viewport coordinates) and writes the
   *   result as `top`/`left` on a `.cdk-overlay-pane` that lives inside
   *   `.cdk-overlay-container`. The math only works when the overlay container
   *   is `position: fixed` (CDK's default), because a fixed container's
   *   containing block IS the viewport.
   *
   *   In page-scroll mode our modal is taller than the viewport and needs to
   *   participate in document flow so the page can scroll it. Previously we
   *   achieved that by overriding `.cdk-overlay-container` to `position: absolute;
   *   min-height: 100vh;` — which broke CDK's contract: connected panels
   *   received viewport-relative coordinates but rendered inside a
   *   document-anchored container, so `mat-select` / `mat-autocomplete` panels
   *   opened while scrolled appeared to "float" far from their trigger inputs
   *   and drifted further as the user kept scrolling.
   *
   *   To satisfy both requirements we keep `.cdk-overlay-container` at its
   *   default `position: fixed` (so Material overlays anchor correctly) and
   *   instead move the modal's own wrapper + backdrop out into `<body>`, where
   *   they can live in document flow. Nested `mat-select` / `mat-autocomplete`
   *   panels opened from inside the modal are still attached to the singleton,
   *   viewport-fixed overlay container and therefore position correctly.
   *
   * Why the transition-suppression dance:
   *   `.td-wb-modal` uses a CSS `transition` with `@starting-style { opacity: 0 }`
   *   to fade in on open. Re-inserting the wrapper subtree into a new parent is
   *   treated by the browser as a fresh element lifecycle, so `@starting-style`
   *   fires again and the modal visibly re-fades. Suppressing transitions across
   *   the move avoids that flicker.
   *
   * High-level steps:
   *   1. Resolve where each element should end up (body vs. original parent).
   *   2. Skip out if nothing actually needs to move.
   *   3. Tag both elements with a class that disables all transitions/animations
   *      on themselves and their descendants (see `_modals.scss`).
   *   4. Perform the DOM moves and update our "original parent" bookkeeping.
   *   5. Flush layout so the browser commits the "no transitions" state against
   *      the new tree, then re-enable transitions on the next animation frame.
   *
   * @param wrapper  The `.cdk-global-overlay-wrapper` for this modal, if present.
   * @param backdrop The `.cdk-overlay-backdrop` for this modal, if present.
   * @param toBody   `true` when entering page-scroll mode (move into `<body>`);
   *                 `false` when exiting (restore to the CDK overlay container).
   */
  private reparentWithoutTransitionReplay(wrapper: HTMLElement | null,
                                          backdrop: HTMLElement | null,
                                          toBody: boolean): void {
    // Step 1: figure out the destination for each element. On enter we always
    // move into <body>; on exit we go back to whatever container the element
    // originally lived in (captured during the enter transition).
    const wrapperTarget: HTMLElement | null = toBody ? this.document.body : this.originalWrapperParent;
    const backdropTarget: HTMLElement | null = toBody ? this.document.body : this.originalBackdropParent;

    const wrapperMove: boolean = this.needsMove(wrapper, wrapperTarget);
    const backdropMove: boolean = this.needsMove(backdrop, backdropTarget);

    // Step 2: nothing to do if both elements are already in the right place.
    if (!wrapperMove && !backdropMove) {
      return;
    }

    // Step 3: prevent transition/@starting-style replay on the elements we're
    // about to reparent. The suppression is released again after the move.
    const suppressed: Array<HTMLElement> = this.suppressTransitions(
      wrapperMove ? wrapper : null,
      backdropMove ? backdrop : null
    );

    // Step 4: perform the actual DOM moves. Wrapper first, then backdrop, so
    // that when we're moving into <body> the backdrop can be inserted directly
    // before the wrapper (matching CDK's default paint order).
    if (wrapperMove && wrapper && wrapperTarget) {
      this.moveWrapper(wrapper, wrapperTarget, toBody);
    }
    if (backdropMove && backdrop && backdropTarget) {
      this.moveBackdrop(backdrop, backdropTarget, wrapper, toBody);
    }

    // On exit, clear the remembered original parents so a subsequent enter
    // captures fresh values (the CDK overlay container is a singleton, but
    // being defensive here avoids any subtle stale-reference bugs).
    if (!toBody) {
      this.originalWrapperParent = null;
      this.originalBackdropParent = null;
    }

    // Step 5: hand off to the release helper, which flushes layout and then
    // clears the suppression class on the next frame.
    this.releaseTransitionSuppression(suppressed);
  }

  /** True when `el` exists and is not already a child of `target`. */
  private needsMove(el: HTMLElement | null, target: HTMLElement | null): boolean {
    return !!(el && target && el.parentElement !== target);
  }

  /**
   * Adds the transition-suppression class to any provided elements and returns
   * the list of elements we tagged, so the caller can release them later.
   */
  private suppressTransitions(...elements: Array<HTMLElement | null>): Array<HTMLElement> {
    const suppressed: Array<HTMLElement> = [];
    for (const el of elements) {
      if (el) {
        el.classList.add('td-wb-modal-suppress-transitions');
        suppressed.push(el);
      }
    }
    return suppressed;
  }

  /**
   * Moves the overlay wrapper to its new parent. When moving into `<body>`
   * (entering page-scroll mode) we also record where it came from so that
   * `exitPageScrollMode()` can put it back.
   */
  private moveWrapper(wrapper: HTMLElement, target: HTMLElement, toBody: boolean): void {
    if (toBody) {
      this.originalWrapperParent = wrapper.parentElement;
    }
    target.appendChild(wrapper);
  }

  /**
   * Moves the backdrop to its new parent. When moving into `<body>` we also
   * record its original parent and insert it directly before the wrapper so
   * that paint order matches CDK's default (backdrop behind, pane in front).
   */
  private moveBackdrop(backdrop: HTMLElement,
                       target: HTMLElement,
                       wrapper: HTMLElement | null,
                       toBody: boolean): void {
    if (toBody) {
      this.originalBackdropParent = backdrop.parentElement;
      target.insertBefore(backdrop, wrapper ?? null);
    } else {
      target.appendChild(backdrop);
    }
  }

  /**
   * Forces a synchronous layout read against the freshly-moved subtree so the
   * browser commits the "no transitions" state under the new parent, and then
   * removes the suppression class on the next animation frame. Removing on the
   * next frame (instead of immediately) guarantees the browser has painted at
   * least one frame with transitions disabled before re-enabling them.
   */
  private releaseTransitionSuppression(suppressed: Array<HTMLElement>): void {
    if (suppressed.length === 0) {
      return;
    }
    // Reading `offsetHeight` is a well-known trick to force the browser to
    // flush pending style/layout calculations synchronously.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    suppressed[0].offsetHeight;
    this.window.requestAnimationFrame((): void => {
      for (const el of suppressed) {
        el.classList.remove('td-wb-modal-suppress-transitions');
      }
    });
  }

  private scrollIfRequired(previouslyFocusedElement: Element | null): void {
    if (previouslyFocusedElement && previouslyFocusedElement instanceof HTMLElement) {
      const currentScrollY = this.window.scrollY || this.document.documentElement.scrollTop || 0;
      const elementTopAbsolute = previouslyFocusedElement.getBoundingClientRect().top + currentScrollY;
      const isBodyElement = previouslyFocusedElement === this.document.body;

      // Only scroll if the previously focused element is above the current scroll position.
      if (elementTopAbsolute < currentScrollY || isBodyElement) {
        const block = isBodyElement ? 'start' : 'center';
        previouslyFocusedElement.scrollIntoView({ behavior: 'smooth', block });
      }
    }
  }

  private scheduleScrollStateUpdate(): void {
    if (this.resizeObserverPending) {
      return;
    }
    this.resizeObserverPending = true;
    // Use requestAnimationFrame to batch multiple resize notifications within the same frame
    // and prevent ResizeObserver loop errors caused by synchronous DOM mutations
    this.window.requestAnimationFrame(() => {
      this.resizeObserverPending = false;
      if (this.isEnabled) {
        this.updateScrollState();
      }
    });
  }
}
